import React, {useState, useEffect, useCallback} from 'react';
import { 
    CalendarIcon, 
    UsersIcon, 
    XMarkIcon, 
    ExclamationTriangleIcon,
    CheckCircleIcon,
    TrashIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import config from '../config';
import Helper from "../helpers";

const baseUrl = config.apiBaseUrl;

const ManageSession = (props) => {
    const {handleClose, Values} = props;
    const [sessionName, setSessionName] = useState('');
    const [availableSeats, setAvailableSeats] = useState(0);
    const [users, setUsers] = useState([]);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isPastSession, setIsPastSession] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sessionDetails, setSessionDetails] = useState(null);

    const fetchSessionDetails = useCallback(async () => {
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Sessions/session/${Values.sessionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                setSessionName(data.name);
                setAvailableSeats(data.availableSeats);
                setUsers(data.users || []);
                setSessionDetails(data);
                // Check if session is in the past
                setIsPastSession(new Date(data.startDate) < new Date());
            } else {
                setError('Failed to fetch session details');
            }
        } catch (error) {
            console.error('Error fetching session details:', error);
            setError('Failed to fetch session details');
        }
    }, [Values.sessionId]);
    
    useEffect(() => {
        fetchSessionDetails();
    }, [fetchSessionDetails]);

    if(!sessionDetails){
        console.warn("Session details not loaded yet");
    }
    
    const handleUpdateSession = async () => {
        setMessage('');
        setError('');
        setLoading(true);

        if (isPastSession) {
            setError('Cannot update sessions that are in the past');
            setLoading(false);
            return;
        }

        try {
            const updatedSession = {
                availableSeats: availableSeats,
            };
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Sessions/update/${Values.sessionId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(updatedSession),
            });

            if (response.ok) {
                setMessage('Session updated successfully');
                setTimeout(() => {
                    handleClose();
                    window.location.reload();
                }, 1500);
            } else {
                const text = await response.text();
                throw new Error(text || 'Failed to update session');
            }
        } catch (error) {
            console.error('Error updating session:', error);
            setError(error.message || 'Failed to update session');
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSession = async () => {
        let confirmed = window.confirm('Are you sure you want to cancel this session? This action cannot be undone.');
        if (!confirmed) {
            return;
        }

        setMessage('');
        setError('');
        setLoading(true);

        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${baseUrl}/api/Sessions/admin/delete/${Values.sessionId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });

            if (response.ok) {
                setMessage('Session cancelled successfully');
                setTimeout(() => {
                    handleClose();
                    window.location.reload();
                }, 1500);
            } else {
                throw new Error('Failed to cancel session');
            }
        } catch (error) {
            console.error('Error cancelling session:', error);
            setError('Failed to cancel session');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-luxury p-6 sm:p-8 max-w-3xl mx-auto animate-slide-up">
            {/* Header */}
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h2 className="text-2xl sm:text-3xl font-display font-bold text-gradient-emerald mb-2">
                        Manage Session
                    </h2>
                    <p className="text-sm text-emerald-600/70 font-body">
                        Update session details and view attendees
                    </p>
                </div>
                <button
                    onClick={handleClose}
                    className="text-emerald-600/60 hover:text-emerald-300 transition-colors duration-300"
                >
                    <XMarkIcon className="h-6 w-6" />
                </button>
            </div>

            {/* Past Session Warning */}
            {isPastSession && (
                <div className="mb-6 p-4 glass-luxury rounded-xl border border-performance-energy/30 bg-gradient-to-br from-performance-energy/5 to-luxury-obsidian/80">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-performance-energy flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm sm:text-base text-performance-energy font-body font-medium">
                                This session is in the past and cannot be edited.
                            </p>
                            <p className="text-xs text-performance-energy/70 font-body mt-1">
                                You can only view session details and attendees.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Session Name (Read-only) */}
            <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                    <CalendarIcon className="h-4 w-4" />
                    Session Name
                </label>
                <div className="glass-luxury rounded-xl p-4 border border-emerald-600/20 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60">
                    <p className="text-base font-display font-semibold text-accent-platinum">{sessionName}</p>
                    <p className="text-xs text-emerald-600/60 font-body mt-1">Session name cannot be changed</p>
                </div>
            </div>

            {/* Available Seats */}
            <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                    <UsersIcon className="h-4 w-4" />
                    Available Seats
                </label>
                <input
                    type="number"
                    value={availableSeats}
                    onChange={(e) => setAvailableSeats(Math.max(0, Number(e.target.value) || 0))}
                    disabled={isPastSession}
                    min="0"
                    className={`input-field w-full ${isPastSession ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                <p className="text-xs text-emerald-600/60 font-body mt-2">
                    Current attendees: {users.length} • Available seats: {Math.max(0, availableSeats - users.length)}
                </p>
            </div>

            {/* Users in Session */}
            <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-display font-semibold text-emerald-300/80 mb-3 uppercase tracking-wide">
                    <UsersIcon className="h-4 w-4" />
                    Attendees ({users.length})
                </label>
                {users.length > 0 ? (
                    <div className="glass-luxury rounded-xl border border-emerald-600/20 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60 overflow-hidden">
                        <div className="max-h-48 overflow-y-auto scrollbar-luxury">
                            {users.map((user, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center justify-between p-3 border-b border-emerald-600/10 last:border-b-0 hover:bg-emerald-600/5 transition-colors"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center shadow-luxury">
                                            <span className="text-xs font-display font-medium text-white">
                                                {user.name?.charAt(0) || 'U'}
                                            </span>
                                        </div>
                                        <span className="text-sm font-body text-accent-platinum">{user.name}</span>
                                    </div>
                                    <span className="inline-flex px-2 py-1 text-xs font-display font-semibold rounded-full bg-emerald-600/20 text-emerald-300 border border-emerald-600/30">
                                        Booked
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="glass-luxury rounded-xl border border-emerald-600/20 bg-gradient-to-br from-luxury-slate/40 to-luxury-obsidian/60 p-8 text-center">
                        <UsersIcon className="h-12 w-12 text-emerald-600/50 mx-auto mb-2" />
                        <p className="text-sm text-emerald-600/70 font-body">No attendees yet</p>
                    </div>
                )}
            </div>

            {/* Success/Error Messages */}
            {message && (
                <div className="mb-6 p-4 glass-luxury rounded-xl border border-emerald-600/30 bg-gradient-to-br from-emerald-600/10 to-luxury-obsidian/80">
                    <div className="flex items-start gap-3">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-emerald-300 font-body font-medium">{message}</p>
                    </div>
                </div>
            )}

            {error && (
                <div className="mb-6 p-4 glass-luxury rounded-xl border border-performance-pulse/30 bg-gradient-to-br from-performance-pulse/5 to-luxury-obsidian/80">
                    <div className="flex items-start gap-3">
                        <ExclamationTriangleIcon className="h-5 w-5 text-performance-pulse flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-performance-pulse font-body font-medium">{error}</p>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <div className="flex-column flex gap-3 w-full">
                    <button
                        onClick={handleCancelSession}
                        disabled={isPastSession || loading}
                        className={`flex-1 order-2 sm:order-2 ${
                            isPastSession || loading
                                ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-6 rounded-xl font-display font-semibold transition-all duration-300'
                                : 'btn-secondary text-performance-pulse border-performance-pulse/50 hover:border-performance-pulse/80 hover:shadow-[0_0_30px_rgba(255,7,58,0.25)]'
                        } flex items-center justify-center gap-2`}
                    >
                        <TrashIcon className="h-4 w-4" />
                        <span>{loading ? 'Cancelling...' : 'Cancel Session'}</span>
                    </button>
                <button
                    onClick={handleClose}
                    className="btn-outline flex-1 order-3 sm:order-1"
                    disabled={loading}
                >
                    Close
                </button>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8`">
                <button
                    onClick={handleUpdateSession}
                    disabled={isPastSession || loading}
                    className={`flex-1 order-1 sm:order-3 ${
                        isPastSession || loading
                            ? 'bg-luxury-graphite/50 text-accent-platinum/50 cursor-not-allowed border border-emerald-600/20 py-3 px-6 rounded-xl font-display font-semibold transition-all duration-300'
                            : 'btn-primary'
                    } flex items-center justify-center gap-2`}
                >
                    <PencilIcon className="h-4 w-4" />
                    <span>{loading ? 'Updating...' : 'Update Session'}</span>
                </button>
               
               
            </div>
            </div>
        </div>
    );
};

export default ManageSession;
